import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth'
import { auth } from '@/config/firebase'
import { onSnapshot, type Unsubscribe } from 'firebase/firestore'
import {
  toSchoolAccount,
  type SchoolAccount,
  type SchoolAccountDoc,
} from '@common/models/account/school/school-account.modl'
import { refs } from '@common/models/base/firestore-refs'
import { SchoolAccountService } from '@common/services/school-account-service'
import { syncSentrySchoolTags } from '@/utils/sentry-school-context'

type SchoolAccountContextType = {
  schoolAccount: SchoolAccount | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
};

const SchoolAccountContext = createContext<SchoolAccountContextType>({
  schoolAccount: null,
  isLoading: true,
  error: null,
  refresh: async () => {},
})

const buildLoginUrl = () => {
  const base = import.meta.env.BASE_URL ?? '/'
  const normalizedBase = base.endsWith('/') ? base : `${base}/`
  return `${normalizedBase}login`
}

export const SchoolAccountProvider = ({ children }: { children: ReactNode }) => {
  const [schoolAccount, setSchoolAccount] = useState<SchoolAccount | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const isSigningOutRef = useRef(false)

  const handleDeactivatedAccount = async () => {
    if (isSigningOutRef.current) return
    isSigningOutRef.current = true
    setSchoolAccount(null)
    setError(new Error('このアカウントは無効化されています'))
    try {
      await firebaseSignOut(auth)
    } catch (err) {
      console.error('利用停止アカウントのログアウトに失敗しました:', err)
    }

    // 利用停止時は画面キャッシュを残さない
    try {
      sessionStorage.clear()
      localStorage.clear()
    } catch {
      // no-op
    }
    if ('caches' in window) {
      try {
        const keys = await window.caches.keys()
        await Promise.all(keys.map((k) => window.caches.delete(k)))
      } catch {
        // no-op
      }
    }
    window.location.replace(buildLoginUrl())
  }

  const fetchSchoolAccount = async (uid: string, includeDeleted = false) => {
    try {
      // ログアウト後の再ログイン直後などで古いトークンが使われると
      // Firestore で permission-denied になることがあるため、
      // 対応するユーザーが currentUser ならトークンを明示的に更新してから読み込む
      const currentUser = auth.currentUser
      if (currentUser && currentUser.uid === uid) {
        await currentUser.getIdToken(true)
      }

      const schoolAccountService = new SchoolAccountService()
      const account = await schoolAccountService.getSchoolAccount(uid, includeDeleted)

      if (!account) {
        setSchoolAccount(null)
        setError(new Error('学校アカウントが見つかりません'))
        return
      }

      if (account.is_deleted) {
        await handleDeactivatedAccount()
        return
      }

      setSchoolAccount(account)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch school account:', err)
      setError(err instanceof Error ? err : new Error('学校アカウントの取得に失敗しました'))
      setSchoolAccount(null)
    }
  }

  const refresh = async () => {
    const user = auth.currentUser
    if (user?.uid) {
      setIsLoading(true)
      await fetchSchoolAccount(user.uid, true)
      setIsLoading(false)
    } else {
      setSchoolAccount(null)
      setError(null)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    syncSentrySchoolTags(schoolAccount)
  }, [schoolAccount])

  useEffect(() => {
    let schoolAccountUnsubscribe: Unsubscribe | null = null

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (schoolAccountUnsubscribe) {
        schoolAccountUnsubscribe()
        schoolAccountUnsubscribe = null
      }
      setIsLoading(true)

      if (user?.uid) {
        // 初回表示は既存処理で取得し、以後は stream で追従する
        await fetchSchoolAccount(user.uid, true)

        schoolAccountUnsubscribe = onSnapshot(
          refs.schoolAccount(user.uid),
          (docSnap) => {
            if (!docSnap.exists()) {
              setSchoolAccount(null)
              setError(new Error('学校アカウントが見つかりません'))
              return
            }
            const account = toSchoolAccount(
              docSnap.id,
              docSnap.data() as SchoolAccountDoc,
              docSnap.ref.path,
            )
            if (account.is_deleted) {
              void handleDeactivatedAccount()
              return
            }
            setSchoolAccount(account)
            setError(null)
          },
          (snapshotError) => {
            console.error('Failed to subscribe school account:', snapshotError)
          },
        )
      } else {
        isSigningOutRef.current = false
        setSchoolAccount(null)
        setError(null)
      }

      setIsLoading(false)
    })

    // クリーンアップ関数
    return () => {
      if (schoolAccountUnsubscribe) {
        schoolAccountUnsubscribe()
      }
      unsubscribe()
    }
  }, [])

  return (
    <SchoolAccountContext.Provider value={{ schoolAccount, isLoading, error, refresh }}>
      {children}
    </SchoolAccountContext.Provider>
  );
};

export const useSchoolAccount = () => {
  const context = useContext(SchoolAccountContext);
  if (!context) {
    throw new Error('useSchoolAccount must be used within a SchoolAccountProvider');
  }
  return context;
};
